import React, {
    FC,
    useEffect,
    useRef,
    useState,
} from "react";
import {Box} from "@mui/material";
import {TwoWheeler} from "@mui/icons-material";
import {IMotorcycle} from "../../../../interfaces/Interfaces";
import {FullscreenOverlay} from "./FullscreenOverlay";
import {ThumbnailList} from "./ThumbnailList";
import {MainImage} from "./MainImage";

interface MotorcycleGalleryProps {
    motorcycle: IMotorcycle;
}

export const MotorcycleGallery: FC<MotorcycleGalleryProps> = ({motorcycle}) => {
    const images = motorcycle?.imagesArray || [];
    const [mainIndex, setMainIndex] = useState<number>(0);
    const [errorIndices, setErrorIndices] = useState<number[]>([]);
    const [isFullscreen, setIsFullscreen] = useState<boolean>(false);
    const thumbnailContainerRef = useRef<HTMLDivElement>(null);

    const handleError = (index: number) => {
        setErrorIndices((prev) => [...prev, index]);
    };

    useEffect(() => {
        document.body.style.overflow = isFullscreen ? "hidden" : "";
        return () => {
            document.body.style.overflow = "";
        };
    }, [isFullscreen]);

    if (images.length === 0) {
        return (
            <Box
                sx={{
                    display: "flex",
                    justifyContent: "center",
                    alignItems: "center",
                    borderRadius: 2,
                    boxShadow: 3,
                    width: "100%",
                    height: 300,
                    backgroundColor: "#f0f0f0",
                }}
            >
                <TwoWheeler sx={{fontSize: "4rem", color: "#ccc"}}/>
            </Box>
        );
    }

    const mainImageName = images[mainIndex];
    const mainImageSrc = `https://fyd.ge/fyd-images/${motorcycle.makerName}/${mainImageName}`;

    const handleScrollLeft = () => {
        if (thumbnailContainerRef.current) {
            thumbnailContainerRef.current.scrollBy({left: -100, behavior: "smooth"});
        }
    };

    const handleScrollRight = () => {
        if (thumbnailContainerRef.current) {
            thumbnailContainerRef.current.scrollBy({left: 100, behavior: "smooth"});
        }
    };

    const handleFullscreenLeft = () => {
        setMainIndex((prev) => (prev > 0 ? prev - 1 : images.length - 1
        ));
    };

    const handleFullscreenRight = () => {
        setMainIndex((prev) => (prev < images.length - 1 ? prev + 1 : 0
        ));
    };

    const openFullscreen = () => {
        setIsFullscreen(true);
    };

    const closeFullscreen = () => {
        setIsFullscreen(false);
    };

    return (
        <Box>
            <Box
                sx={{
                    position: "relative",
                    width: "100%",
                    height: 300,
                    borderRadius: 2,
                    boxShadow: 3,
                    backgroundColor: "#fff",
                    mb: 2,
                    display: "flex",
                    justifyContent: "center",
                    alignItems: "center",
                    cursor: "pointer",
                }}
                onClick={openFullscreen}
            >
                <MainImage
                    src={mainImageSrc}
                    alt={`${motorcycle.makerName} ${motorcycle.modelName} - ${mainIndex}`}
                    onError={() => handleError(mainIndex)}
                    error={errorIndices.includes(mainIndex)}
                    sx={{
                        width: "100%",
                        height: "100%",
                        objectFit: "contain",
                        borderRadius: 2,
                    }}
                />
            </Box>
            <ThumbnailList
                images={images}
                motorcycleName={motorcycle.makerName}
                mainIndex={mainIndex}
                setMainIndex={setMainIndex}
                errorIndices={errorIndices}
                handleError={handleError}
                containerRef={thumbnailContainerRef}
                scrollLeft={handleScrollLeft}
                scrollRight={handleScrollRight}
            />
            {isFullscreen && (
                <FullscreenOverlay
                    motorcycle={motorcycle}
                    images={images}
                    mainIndex={mainIndex}
                    setMainIndex={setMainIndex}
                    errorIndices={errorIndices}
                    handleError={handleError}
                    closeFullscreen={closeFullscreen}
                    handleFullscreenLeft={handleFullscreenLeft}
                    handleFullscreenRight={handleFullscreenRight}
                    thumbnailContainerRef={thumbnailContainerRef}
                />
            )}
        </Box>
    );
};
